// Generic sortable-table logic — the pure core of the legacy table-sort.js,
// reused by the React useSortableTable hook. Framework-free and unit-tested.
//
// Faithful to the legacy behaviour: clicking a column sorts by it; clicking the
// same column again flips direction; the first click on a fresh column uses a
// per-column default direction (text and "lowest is best" columns start
// ascending, everything else descending). Values are compared as text
// (localeCompare) or numbers (parseFloat), matching how the legacy read cell
// text / data-val.

export interface SortableColumn {
  key: string;
  /** How to compare: alphabetical ("text") or numeric ("num"). */
  type: "text" | "num";
  /** Lowest value is "best", so the first click sorts ascending (e.g. GA, L). */
  lowFirst?: boolean;
}

/** null key = original (unsorted) order — what the "#" rank header restores. */
export interface SortState {
  key: string | null;
  asc: boolean;
}

/** Direction the first click on a fresh column uses. */
export function defaultAsc(col: SortableColumn): boolean {
  return col.type === "text" || col.lowFirst === true;
}

/** Next sort state when a column header is clicked: toggle if it's already
 *  active, else the column's default direction. */
export function nextSort(prev: SortState, col: SortableColumn): SortState {
  if (prev.key === col.key) return { key: col.key, asc: !prev.asc };
  return { key: col.key, asc: defaultAsc(col) };
}

/**
 * A stable copy of `rows` sorted by `col`. `value` reads the sortable value
 * from a row (the displayed text for text columns, a number for numeric ones);
 * numeric values are coerced with parseFloat, matching the legacy. Returns a
 * new array — the caller keeps the original order for "restore".
 */
export function sortByColumn<T>(
  rows: readonly T[],
  col: SortableColumn,
  asc: boolean,
  value: (row: T) => string | number
): T[] {
  const dir = asc ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (col.type === "text") {
      return dir * String(value(a)).localeCompare(String(value(b)));
    }
    const na = toNum(value(a));
    const nb = toNum(value(b));
    return dir * (na - nb);
  });
}

function toNum(v: string | number): number {
  if (typeof v === "number") return v;
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}
